import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useState } from 'react'

import { useGrantsUtils } from '../../utils/utils'

import { useGrantsPermissions } from '@percent/admin-dashboard/app/grants/hooks/useGrantsPermissions'
import { AddProgramModal } from './addProgramModal/AddProgramModal'
import { handleClickAsLink } from '@percent/admin-dashboard/common/utility/handleClickAsLink'
import {
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TablePagination,
  TableProvider,
  TableRow,
  TableSearchAndFilters,
  Flex,
  SearchFilterList,
  Button
} from '@percent/lemonade'
import { GrantsProgramsTableProps } from './GrantsProgramsTable.types'
import { useProgramNameSearch } from './hooks/useProgramNameSearch'
import { usePartnerFilter } from './hooks/usePartnerFilter'

export function GrantsProgramsTable({
  data,
  totalResults,
  isLoading,
  nextPage,
  previousPage,
  queryParams,
  setQueryParams,
  onProgramAction
}: Readonly<GrantsProgramsTableProps>) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [canCreateProgram] = useGrantsPermissions(['can_create_program'])
  const { formatCurrency, formatDate } = useGrantsUtils()
  const { programName, programNameSearch } = useProgramNameSearch({ queryParams, setQueryParams })
  const { selectedPartner, partnerNameOptions, onPartnerChange, isPartnerNameFilterLoading, partnerSearch } =
    usePartnerFilter({ queryParams, setQueryParams })
  const [isAddProgramModalOpen, setIsAddProgramModalOpen] = useState(false)

  const columns: TableColumn[] = [
    { id: 'name', content: t('table.programName'), isSortable: false, props: { width: '50%' } },
    { id: 'budgetMin', content: t('table.budgetMin'), isSortable: false, props: { width: '10%' } },
    { id: 'budgetMax', content: t('table.budgetMax'), isSortable: false, props: { width: '10%' } },
    { id: 'closesAt', content: t('table.closesAt'), isSortable: false, props: { width: '10%' } },
    { id: 'createdAt', content: t('table.createdAt'), isSortable: false, props: { width: '20%' } }
  ]

  return (
    <TableProvider
      columns={columns}
      emptyTableTitle={t('table.noProgramsFound')}
      illustration="no-results"
      isLoading={isLoading}
      totalResults={totalResults}
    >
      <TableSearchAndFilters>
        <Flex w="100%" maw="480" mr={8}>
          <SearchInput
            name="programName"
            value={programName}
            handleClearValue={programNameSearch.handleClearProgramNameValue}
            onKeyDown={programNameSearch.handleKeyPress}
            onChange={programNameSearch.onProgramNameChange}
            onBlur={programNameSearch.validateProgramName}
            placeholder={t('table.programNameSearchPlaceholder')}
            data-testid="program-name-search-input"
            status="default"
            inputSize="small"
          />
          <Flex align="center" ml={8}>
            <SearchFilterList
              options={partnerNameOptions}
              isLoading={isPartnerNameFilterLoading}
              selectedValues={selectedPartner ? [selectedPartner] : []}
              onChange={onPartnerChange}
              searchValue={partnerSearch.partnerNameSearch}
              onSearchChange={partnerSearch.onPartnerSearchChange}
              handleClearValue={partnerSearch.onClearPartnerSearchValue}
              onKeyDown={partnerSearch.handleKeyPress}
              onButtonClick={partnerSearch.onPartnersSearchButtonClick}
              testId="partner-search-filter"
              buttonText={t('typography.search')}
              placeholder={t('typography.search')}
              label={t('typography.partner')}
              placement="bottom"
            />
          </Flex>
        </Flex>
        {canCreateProgram && (
          <Flex w="100%" justify="flex-end" align="center">
            <Button variant="secondary" size="small" onClick={() => setIsAddProgramModalOpen(true)}>
              {t('button.add')}
            </Button>
          </Flex>
        )}
      </TableSearchAndFilters>

      <Table>
        <TableBody>
          {data?.map(program => (
            <TableRow key={program.id} onClick={handleClickAsLink(`/grants-programs/${program.id}`, navigate)}>
              <TableCell>{program.name}</TableCell>
              <TableCell>{formatCurrency(program.budgetMin, program.currency)}</TableCell>
              <TableCell>{formatCurrency(program.budgetMax, program.currency)}</TableCell>
              <TableCell>{formatDate(program.closesAt)}</TableCell>
              <TableCell>{formatDate(program.createdAt)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <AddProgramModal open={isAddProgramModalOpen} setOpen={setIsAddProgramModalOpen} onAdd={onProgramAction} />
      <TablePagination
        nextPage={nextPage}
        previousPage={previousPage}
        totalResultsText={t('table.results', { count: totalResults ?? 0 })}
      />
    </TableProvider>
  )
}
